import React, { useRef, useEffect, useState, useCallback } from 'react';

// Individual SignaturePad component with enhancements
const SignaturePad = ({ 
  onSave, 
  onClear, 
  width = 400, 
  height = 200,
  minStrokes = 1,  // Minimum number of strokes required
  label = "Signature",
  instructionText = "Sign above using your mouse or touch device",
  initialSignature = null // To support pre-filled signatures if needed
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);
  const [strokeCount, setStrokeCount] = useState(0);
  const [pixelCount, setPixelCount] = useState(0);
  const [isValid, setIsValid] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    setContext(ctx);
    
    
    
    
  }, [width, height, initialSignature]);

  // Validate signature based on stroke count and pixel density
  useEffect(() => {
    // If we have at least the minimum number of strokes and some pixels drawn
    const valid = strokeCount >= minStrokes && pixelCount > 50;
    setIsValid(valid);
    
    // If signature becomes valid, automatically save it
    if (valid && onSave && canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onSave(dataUrl);
    }
  }, [strokeCount, pixelCount, minStrokes, onSave]);

  // Calculate drawn pixel count (rough approximation)
  const calculatePixelDensity = useCallback(() => {
    if (!canvasRef.current || !context) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    let count = 0;
    // Every 4 indices represent RGBA values for one pixel
    for (let i = 3; i < data.length; i += 4) {
      // If the alpha channel is greater than 0, the pixel has been drawn on
      if (data[i] > 0) {
        count++;
      }
    }
    
    setPixelCount(count);
  }, [context]);

  

  // Clear canvas function
  const clearCanvas = useCallback(() => {
    if (context) {
      context.clearRect(0, 0, width, height);
      context.beginPath();
      setStrokeCount(0);
      setPixelCount(0);
      setIsValid(false);
      
      if (onClear) onClear();
    }
  }, [context, height, width, onClear]);

  

  // Drawing handlers
  const startDrawing = useCallback((e) => {
    if (!context) return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    context.beginPath();
    context.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  }, [context]);

  const draw = useCallback((e) => {
    if (!isDrawing || !context) return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    context.lineTo(offsetX, offsetY);
    context.stroke();
  }, [isDrawing, context]);

  const stopDrawing = useCallback(() => {
    if (isDrawing && context) {
      context.closePath();
      setIsDrawing(false);
      
      // Increment stroke count and calculate pixel density
      setStrokeCount(prevCount => prevCount + 1);
      calculatePixelDensity();
    }
  }, [isDrawing, context, calculatePixelDensity]);

  // Handle touch events
  // Updated function
const getCoordinates = (event) => {
  if (!canvasRef.current) return { offsetX: 0, offsetY: 0 };
  
  const canvas = canvasRef.current;
  const rect = canvas.getBoundingClientRect();
  
  if (event.touches && event.touches[0]) {
    // Touch event
    return {
      offsetX: event.touches[0].clientX - rect.left,
      offsetY: event.touches[0].clientY - rect.top
    };
  } else if (event.nativeEvent) {
    // Mouse event - use clientX/Y instead of offsetX/Y for consistency
    return {
      offsetX: event.nativeEvent.clientX - rect.left,
      offsetY: event.nativeEvent.clientY - rect.top
    };
  } else {
    // Fallback for other event types
    return {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
  }
};

  return (
    <div className="signature-pad-container">
      <div className="mb-2 text-sm font-medium">{label}</div>
      <div className="mb-2 text-sm text-gray-600">{instructionText}</div>
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        style={{ 
          border: isValid ? '1px solid #4CAF50' : '1px solid #ccc', 
          backgroundColor: '#fff',
          borderRadius: '4px',
          touchAction: 'none',  // Prevents scrolling on touch devices
          width: '100%',
          height: `${height}px`
        }}
      />
      <div className="flex justify-between items-center mt-1">
        <div>
          {!isValid && strokeCount > 0 && (
            <div className="text-red-500 text-sm">
              Signature not valid. Please make your signature more distinct.
            </div>
          )}
          {isValid && (
            <div className="text-green-500 text-sm">
              Signature valid
            </div>
          )}
          {strokeCount === 0 && (
            <div className="text-gray-500 text-sm">
              No signature yet
            </div>
          )}
        </div>
        <button 
          type="button"
          onClick={clearCanvas} 
          className="text-blue-600 text-sm hover:text-blue-800"
        >
          Clear
        </button>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {strokeCount > 0 ? `Strokes: ${strokeCount}` : ""}
        {pixelCount > 0 ? ` | Density: ${pixelCount > 50 ? 'Good' : 'Too light'}` : ""}
      </div>
    </div>
  );
};

export default SignaturePad;